import apollo, { baseUrl, http } from '@/graphql'

import Vue from 'vue'

import SaveStoreDetails from '@/graphql/store/SaveStoreDetails.gql'
import SaveStoreInstallFields from '@/graphql/store/SaveStoreInstallFields.gql'
import SaveStoreInstallLanes from '@/graphql/store/SaveStoreInstallLanes.gql'
import SaveStoreNote from '@/graphql/store_note/SaveStoreNote.gql'
import SaveStoreWifi from '@/graphql/store/SaveStoreWifi.gql'
import SetStoreInstalled from '@/graphql/store/SetStoreInstalled.gql'
import StoreDetailsQuery from '@/graphql/store/StoreDetailsQuery.gql'
import UnassignedDevicesQuery from '@/graphql/device/UnassignedDevicesQuery.gql'
import SaveAssignedRep from '@/graphql/device_issue/SaveAssignedRep.gql'
import SaveDeviceAssign from '@/graphql/device/SaveDeviceAssign.gql'

// Hotspot
import HotspotStatusListQuery from '@/graphql/store/HotspotStatusListQuery.gql'
import HotspotListQuery from '@/graphql/store/HotspotListQuery.gql'
import HotspotDetailsQuery from '@/graphql/store/HotspotDetailsQuery.gql'
import StoreHotspotDetailsQuery from '@/graphql/store/StoreHotspotDetailsQuery.gql'
import SaveHotspotQuery from '@/graphql/store/SaveHotspot.gql'
import SaveHotspotAssignment from '@/graphql/store/SaveHotspotAssignment.gql'

const urlHeader = process.env.NODE_ENV === 'development' ? `${process.env.BACKEND_API}` :
  `${http}://api.${baseUrl}`

// initial state
const state = {
  editableHotspotFields: {},
  editableStoreHotspotDetails: {},
  editableStoreFields: {},
  isStoreDetailsLoading: false,
  isStoreDetailsSaving: false,
  lanes: [],
  storeClosedIssues: [], // Backend only returns the last 100 closed issues
  storeDetails: {},
  storeLocations: [],
  storeOpenIssues: [],
  isModifiedDeviceIssues: false,
  hotspotStatusList: [],
  hotspotList: [],
  storeAreas: [],
  storePlacements: [],
  placeholderPlacements: [],
  hotspotDetails: {
    id: '',
    name: '',
    status: '',
    ssid: '',
    password: '',
    imei: '',
    simSerialNumber: '',
    deviceSerialNumber: '',
    adminPSK: '',
    mobileNumber: '',
    createdAt: '',
    createdBy: '',
    model: '',
  },
  storeHotspotDetails: {
    id: '',
    name: '',
    notes: '',
  },
}

// getters
const getters = {
  editableHotspotFields: st => st.editableHotspotFields,
  editableStoreHotspotDetails: st => st.editableStoreHotspotDetails,
  editableStoreFields: st => st.editableStoreFields,
  isStoreDetailsLoading: st => st.isStoreDetailsLoading,
  isStoreDetailsSaving: st => st.isStoreDetailsSaving,
  isStoreLocationsLoading: st => st.isStoreLocationsLoading,
  lanes: st => st.lanes,
  storeClosedIssues: st => st.storeClosedIssues,
  storeDetails: st => st.storeDetails,
  storeLocations: st => st.storeLocations,
  storeOpenIssues: st => st.storeOpenIssues,
  isModifiedDeviceIssues: st => st.isModifiedDeviceIssues,
  hotspotStatusList: st => st.hotspotStatusList,
  hotspotList: st => st.hotspotList,
  hotspotDetails: st => st.hotspotDetails,
  storeHotspotDetails: st => st.storeHotspotDetails,
  storeAreas: st => st.storeAreas,
  storePlacements: st => st.storePlacements,
  placeholderPlacements: st => st.placeholderPlacements,
}

// mutations
const mutations = {
  addLane() {
    state.lanes.push({
      devicesId: null,
      leftLaneSide: { isHidden: false },
      number: state.lanes.filter(lane => !lane.isDeleted).length + 1,
      rightLaneSide: { isHidden: false },
      isBeltLeft: true,
      isNumbered: true,
      position: state.lanes.filter(lane => !lane.isDeleted).length,
      isDeleted: false,
    })
  },

  addLocation() {
    const updatedLocations = state.storeLocations.length > 0 ? state.storeLocations : []
    const filteredLocations = updatedLocations.filter(location => !location.deletedAt)
    const maxNumber = filteredLocations.reduce(
      (max, location) => Math.max(max, location.number),
      0,
    )

    const number = updatedLocations.length > 0 ?
      maxNumber + 1 : 1
    const position = updatedLocations.length > 0 ?
      filteredLocations.length : 0

    updatedLocations.unshift({
      devicesId: null,
      storesId: state.storeDetails.id,
      leftLaneSide: { isHidden: false },
      number, // Need to retain number for lanes concurrency
      description: '',
      status: null,
      rightLaneSide: { isHidden: false },
      isCustomerLeft: true,
      position,
      deletedAt: null,
      storeAreaId: null,
      displayRotation: '',
      isNumbered: true,
    })
    Vue.set(state, 'storeLocations', updatedLocations)
  },

  createStoreDetails() {
    state.editableStoreFields = {
      hours: {
        global: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Mon: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Tue: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Wed: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Thu: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Fri: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Sat: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Sun: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
      },
      isActive: true,
      isDhcp: true,
      isFillerEnabled: true,
      isHighPriority: false,
      isInstalled: false,
      isProgrammatic: true,
      isSpanishEmails: false,
      networkSecurityType: 'WPA',
      psk: null,
      wpaePassword: null,
      userNotifications: [],
    }

    state.lanes = []
    state.storeLocations = []

    state.storeDetails = {
      lanes: [],
      name: 'New Store',
      retailer: {},
    }

    state.storeOpenIssues = []
    state.storeClosedIssues = []
  },

  async restoreLocation(st, location) {
    const locationIndex = state.storeLocations.findIndex(loc => loc.id === location.id)
    state.storeLocations[locationIndex].deletedAt = null
  },

  setEditableStoreFields(st, storeDetails) {
    state.editableStoreFields = {
      address: storeDetails.address,
      bannersId: storeDetails.banner.id,
      city: storeDetails.city,
      gateway: storeDetails.gateway,
      hours: JSON.parse(storeDetails.hours),
      id: storeDetails.id,
      installDate: storeDetails.installDate,
      installerNotes: storeDetails.installerNotes,
      isActive: storeDetails.isActive,
      isDhcp: storeDetails.isDhcp,
      isExpectWifi: storeDetails.isExpectWifi,
      isFillerEnabled: storeDetails.isFillerEnabled,
      isHighPriority: storeDetails.isHighPriority,
      isInstalled: storeDetails.isInstalled,
      isInstallerWifiTest: storeDetails.isInstallerWifiTest,
      isProgrammatic: storeDetails.isProgrammatic,
      isSpanishEmails: storeDetails.isSpanishEmails,
      name: storeDetails.name,
      networkPrefixLength: storeDetails.networkPrefixLength,
      networkSecurityType: storeDetails.networkSecurityType || 'WPA', // if null, set to WPA
      wpaEnterpriseUsername: storeDetails.wpaEnterpriseUsername,
      notes: storeDetails.notes,
      phone: storeDetails.phone,
      psk: storeDetails.psk,
      wpaePassword: storeDetails.wpaePassword,
      retailersId: storeDetails.retailer.id,
      ssid: storeDetails.ssid,
      state: storeDetails.state,
      tz: storeDetails.tz,
      uninstallDate: storeDetails.uninstallDate,
      userNotifications: storeDetails.userNotifications.map(userNotification => ({
        emailField: userNotification.emailField,
        notificationsId: userNotification.notificationsId,
        usersId: userNotification.usersId,
        usersName: userNotification.user.name,
      })),
      zip: storeDetails.zip,
      opsManagerId: storeDetails.opsManagerId,
      isAssignedOver72Hours: storeDetails.isAssignedOver72Hours,
      internetType: storeDetails.internetType,
      storeAreaId: storeDetails.storeAreaId,
      storeOrientation: storeDetails.storeOrientation,
      storeAreasId: storeDetails.storeAreasId,
    }
  },

  async setLanes(st, storeDetails) {
    state.lanes = await storeDetails.lanes.map(lane => ({
      id: lane.id,
      ipAddress: lane.ipAddress,
      number: lane.number,
      leftLaneSide: {
        id: lane.leftLaneSide.id,
        isHidden: lane.leftLaneSide.isHidden,
      },
      rightLaneSide: {
        id: lane.rightLaneSide.id,
        isHidden: lane.rightLaneSide.isHidden,
      },
      isBeltLeft: lane.leftLaneSide.isBelt,
      isNumbered: lane.isNumbered,
      devicesId: `${lane.device.id || ''}`, // String
      position: lane.position,
      wifiSSID: lane.device.wifiSSID,
      isDeleted: lane.isDeleted,
    }))
  },

  async setStoreLocations(st, storeLocations) {
    state.storeLocations = storeLocations
  },

  setStoreDetails(st, storeDetails) {
    state.storeDetails = storeDetails
  },

  setStoreIssues(st, storeDetails) {
    const openIssues = []
    const closedIssues = []
    storeDetails.lanes.forEach((lane) => {
      if (lane.device.openIssues.length > 0) {
        openIssues.push(
          ...lane.device.openIssues.map(issue => ({
            ...issue,
            ...{ laneNumber: lane.number, subtypeId: issue.subtype.id },
          })),
        )
      }
      if (lane.device.closedIssues.length > 0) {
        closedIssues.push(
          ...lane.device.closedIssues.map(issue => ({
            ...issue,
            ...{ laneNumber: lane.number, subtypeId: issue.subtype.id },
          })),
        )
      }
    })
    state.storeOpenIssues = openIssues
    state.storeClosedIssues = closedIssues
  },

  setIsModifiedDeviceIssues(st, value) {
    state.isModifiedDeviceIssues = value
  },

  setIsStoreDetailsLoadingState(st, value) {
    state.isStoreDetailsLoading = value
  },

  setIsStoreLocationsLoading(st, value) {
    state.isStoreLocationsLoading = value
  },

  async setStoreAreas(st, storeAreas) {
    state.storeAreas = storeAreas.availableStoreAreasList
  },

  async setStorePlacements(st, storePlacements) {
    state.storePlacements = storePlacements.availableStorePlacementsList
  },

  async setPlaceholderPlacements(st, storePlacements) {
    state.placeholderPlacements = storePlacements.availableStorePlacementsList
      .filter(placement => placement.placementName === 'Pre-Install Placeholder')
  },

  updateStoreFields(st, fields) {
    Object.assign(state.editableStoreFields, fields)
  },

  updateLocation(state, updatedRow) {
    const index = state.storeLocations
      .findIndex(row => row.number === updatedRow.number && row.deletedAt === null)

    if (index !== -1) {
      const existingRow = state.storeLocations[index]
      const newPlacement = state.storePlacements
        .find(placement => placement.placementId === updatedRow.placementId)

      if (newPlacement) {
        const updatedProperties = {
          ...existingRow,
          ...updatedRow,
          placementName: newPlacement.placementName,
          placementDescription: newPlacement.placementDescription,
          storeAreaId: newPlacement.storeAreaId,
        }

        state.storeLocations.splice(index, 1, updatedProperties)
      }
    }
  },

  /** HOTSPOT RELATED FUNCTIONS */
  setEditableHotspotFields(st, hotspotDetails) {
    state.editableHotspotFields = {
      id: hotspotDetails.id,
      name: hotspotDetails.name,
      status: hotspotDetails.status,
      ssid: hotspotDetails.ssid,
      password: hotspotDetails.password,
      imei: hotspotDetails.imei,
      simSerialNumber: hotspotDetails.simSerialNumber,
      deviceSerialNumber: hotspotDetails.deviceSerialNumber,
      adminPSK: hotspotDetails.adminPSK,
      mobileNumber: hotspotDetails.mobileNumber,
      createdAt: hotspotDetails.createdAt,
      createdBy: hotspotDetails.createdBy,
      storeId: hotspotDetails.storeId,
      model: hotspotDetails.model,
    }
  },

  setEditableStoreHotspotDetails(st, storeHotspotDetails) {
    state.editableStoreHotspotDetails = {
      id: storeHotspotDetails.id,
      name: storeHotspotDetails.name,
      notes: storeHotspotDetails.notes,
    }
  },

  setHotspotStatusList(st, value) {
    state.hotspotStatusList = value
  },

  setHotspotList(st, value) {
    state.hotspotList = value
  },

  setHotspotDetails(st, value) {
    state.hotspotDetails = value
  },

  setStoreHotspotDetails(st, value) {
    state.storeHotspotDetails = value
  },
  /** END HOTSPOT RELATED FUNCTIONS */
}

// actions
const actions = {
  async deleteStoreLocation({ dispatch }, { storesId, id }) {
    const requestOptions = {
      method: 'DELETE',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }

    await fetch(`${urlHeader}/v2/store_location/${id}`, requestOptions)
      .catch((err) => {
        throw new Error(`Failed to delete store location: ${err.message}`)
      })

    await dispatch('loadStoreLocations', {
      storesId,
    })
  },

  async loadZones({ commit }) {
    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }

    await fetch(`${urlHeader}/v2/stores/available_areas`, requestOptions)
      .then(response => response.text())
      .then((result) => {
        const jsonResult = JSON.parse(result)
        commit('setStoreAreas', jsonResult)
      })
      .catch((err) => {
        throw new Error(`Failed to get store zones: ${err.message}`)
      })
  },

  async loadZonePlacements({ commit }) {
    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }

    await fetch(`${urlHeader}/v2/stores/available_placements`, requestOptions)
      .then(response => response.text())
      .then((result) => {
        const jsonResult = JSON.parse(result)
        commit('setStorePlacements', jsonResult)
        commit('setPlaceholderPlacements', jsonResult)
      })
      .catch((err) => {
        throw new Error(`Failed to get store placements: ${err.message}`)
      })
  },

  async loadStoreLocations(
    { commit },
    { storesId, keepEditableFields },
  ) {
    commit('setIsStoreLocationsLoading', true)

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }

    if (storesId) {
      await fetch(`${urlHeader}/v2/store/${storesId}/store_locations`, requestOptions)
        .then(response => response.text())
        .then((result) => {
          const jsonResult = JSON.parse(result)
          if (!keepEditableFields) {
            commit('setStoreLocations', jsonResult)
          }
        })
        .catch((err) => {
          throw new Error(`Failed to update store location: ${err.message}`)
        })
    }

    commit('setIsStoreLocationsLoading', false)
  },

  async saveStoreLocations({ commit, state: { storeDetails } }, { storeLocations }) {
    let missingAreaOrientation
    let badPlacement
    storeLocations.forEach((location) => {
      if (!location.id) {
        missingAreaOrientation = !location.placementId || !location.displayRotation
      }
      if (location.devicesId) {
        badPlacement = state.placeholderPlacements
          .find(placement => placement.placementId === location.placementId)
      }
    })

    if (missingAreaOrientation) {
      throw new Error('Missing placement or orientation')
    }

    if (badPlacement) {
      throw new Error('Pre-Install Placeholder is only applicable if location has no device assigned')
    }

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(storeLocations),
    }

    // Need to account for newly created stores
    const storesId = storeDetails.id || storeLocations[0].storesId

    await fetch(`${urlHeader}/v2/store/${storesId}/store_locations`, requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw Error()
        }
        return response.text()
      })
      .then((result) => {
        const jsonResult = JSON.parse(result)
        commit('setStoreLocations', jsonResult)
      })
      .catch((err) => {
        throw new Error('Failed to save store locations', err.message)
      })
  },

  async replaceLocationDevice(
    { dispatch },
    { locationInput },
  ) {
    const requestOptions = {
      method: 'PATCH',
      redirect: 'follow',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(locationInput),
    }

    if (locationInput.id) {
      await fetch(`${urlHeader}/v2/store_location/${locationInput.id}`, requestOptions)
        .catch((err) => {
          throw new Error(`Failed to update store location: ${err.message}`)
        })
    }

    await dispatch('loadStoreLocations', {
      storesId: locationInput.storesId,
    })
  },

  async loadStoreDetails(
    { commit, dispatch },
    { id, initialLoad, keepEditableFields, $message, showSaved },
  ) {
    commit('setIsStoreDetailsLoadingState', true)

    if (initialLoad) {
      Object.assign(state, {
        editableStoreFields: {},
        lanes: [],
        storeDetails: {},
      })
    }

    const {
      data: { storeDetails },
    } = await apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: StoreDetailsQuery,
      variables: { id: +id },
    })

    commit('setStoreDetails', storeDetails)

    await dispatch('loadStoreLocations', {
      storesId: id,
      keepEditableFields,
    })

    if (!keepEditableFields) {
      commit('setEditableStoreFields', storeDetails)
      commit('setLanes', storeDetails)
      commit('setStoreIssues', storeDetails)
    }

    if (showSaved) {
      $message({ message: 'Changes saved', type: 'success' })
      state.isStoreDetailsSaving = false
    }
    commit('setIsStoreDetailsLoadingState', false)
  },

  async loadHotspotStatusList({ commit }) {
    await apollo.defaultClient.query({
      query: HotspotStatusListQuery,
    }).then(({ data: { getHotspotStatusList } }) => {
      commit('setHotspotStatusList', getHotspotStatusList)
    })
  },

  async loadHotspotList({ commit }, { isUnassigned }) {
    await apollo.defaultClient.query({
      query: HotspotListQuery,
      variables: { isUnassigned },
    }).then(({ data: { getHotspotList } }) => {
      commit('setHotspotList', getHotspotList)
    })
  },

  async loadHotspotDetails({ commit }, { id }) {
    if (id) {
      await apollo.defaultClient.query({
        query: HotspotDetailsQuery,
        variables: { id: +id, storeId: +state.storeDetails.id },
      }).then(({ data: { getHotspotDetails } }) => {
        commit('setHotspotDetails', getHotspotDetails)
        commit('setEditableHotspotFields', getHotspotDetails)
        return getHotspotDetails
      })
    } else {
      commit('setHotspotDetails', {})
    }
  },

  async loadStoreHotspotDetails({ commit }, { storeId }) {
    if (storeId) {
      await apollo.defaultClient.query({
        query: StoreHotspotDetailsQuery,
        variables: { storeId: +storeId },
      }).then(({ data: { getStoreHotspotDetails } }) => {
        commit('setStoreHotspotDetails', getStoreHotspotDetails)
        commit('setEditableStoreHotspotDetails', getStoreHotspotDetails)
        return getStoreHotspotDetails
      })
    } else {
      commit('setStoreHotspotDetails', {})
    }
  },

  async saveHotspot({ dispatch }, { fields, $message }) {
    await apollo.defaultClient.mutate({
      mutation: SaveHotspotQuery,
      variables: { fields },
    }).then(({ data: { saveHotspot } }) => {
      $message({ message: 'Changes saved', type: 'success' })
      state.hotspotList.push(saveHotspot)
    })
      .finally(() => {
        dispatch('loadHotspotList', false)
      })
  },

  async saveHotspotAssignment({ dispatch }, { fields, $message }) {
    return apollo.defaultClient.mutate({
      mutation: SaveHotspotAssignment,
      variables: { fields },
    }).then(() => {
      $message({ message: 'Changes saved', type: 'success' })
      dispatch('loadStoreDetails', {
        id: state.storeDetails.id,
        keepEditableFields: true,
      })
    })
  },

  revertHotspotDetailsEdits({
    commit,
    state: {
      hotspotDetails,
      storeHotspotDetails,
    },
  }) {
    commit('setEditableHotspotFields', hotspotDetails)
    commit('setEditableStoreHotspotDetails', storeHotspotDetails)
  },

  // eslint-disable-next-line consistent-return
  async saveStoreDetails({
    dispatch,
    state: {
      editableStoreFields,
      storeDetails,
      storeLocations,
    },
  }, {
    $message,
    $router,
    isReloadPage,
    activeTab,
    removalReason,
    removalNote,
    userId,
  }) {
    state.isStoreDetailsSaving = true
    try {
      const { data: { saveStoreDetails: { id } } } = await apollo.defaultClient
        .mutate({
          mutation: SaveStoreDetails,
          variables: {
            fields: Object.assign({}, editableStoreFields, {
              /**
               * `installDate` expects a String, but sometimes is an epoch long.
               */
              installDate: String(editableStoreFields.installDate),
              uninstallDate: String(editableStoreFields.uninstallDate),
              removalReason,
              removalNote,
              userId,
              method: storeDetails.method,
            }),
          },
          refetchQueries: [{ query: UnassignedDevicesQuery }],
        })

      // Add newly created stores id to store locations
      if (storeLocations[0] && !storeLocations[0].storesId) {
        // eslint-disable-next-line no-param-reassign
        storeLocations = storeLocations.map((location) => {
          // eslint-disable-next-line no-param-reassign
          location.storesId = id
          return location
        })
      }

      if (storeLocations.length > 0) {
        await dispatch('saveStoreLocations', {
          storeLocations,
        })

        await dispatch('loadStoreLocations', {
          storeLocations,
          storesId: id,
        })
      }

      if (isReloadPage) {
        $router.push(`/store/${id}/${activeTab || ''}`)
      }

      if (storeDetails.id || id) {
        await dispatch('loadStoreDetails', {
          id: storeDetails.id ? storeDetails.id : id,
          $message,
          showSaved: true,
        })
      } else {
        $message({ message: 'Changes saved', type: 'success' })
      }
      /* eslint-disable-next-line consistent-return */
      return id
    } catch (err) {
      state.isStoreDetailsSaving = false
      await dispatch('revertStoreDetailsEdits')

      $message.error({
        duration: 0,
        message: err.message,
        showClose: true,
      })
    }
  },

  saveDeviceAssign(root, variables) {
    return apollo.defaultClient
      .mutate({
        mutation: SaveDeviceAssign,
        variables,
      })
  },

  revertStoreDetailsEdits({
    commit,
    state: {
      storeDetails,
    },
  }) {
    commit('setEditableStoreFields', storeDetails)
    commit('setLanes', storeDetails)
    commit('setStoreIssues', storeDetails)
  },

  async saveStoreNote(context, { $message, note, noMessage }) {
    const assignRepToIssue = (issueId, repId) => {
      apollo.defaultClient.mutate({
        mutation: SaveAssignedRep,
        variables: {
          fields: {
            id: issueId,
            assignedRep: repId === 1 // FIXME: Magic number
              ? null // Make null when unassigned
              : repId,
          },
        },
      })
        .catch((err) => {
          throw new Error(`Failed to assign issue ID ${issueId}: ${err.message}`)
        })
    }

    const openIssues = state.storeOpenIssues
      .filter(_ => _.assignedRepUser.id !== state.storeDetails.opsManagerId)
    if (note.isEscalated && state.storeDetails.opsManagerId && openIssues.length > 0) {
      try {
        await Promise.all([
          // eslint-disable-next-line arrow-body-style
          ...openIssues.map((issue) => {
            return assignRepToIssue(
              issue.id,
              state.storeDetails.opsManagerId,
            )
          }),
        ])

        $message({
          message: `Assignment updated for ${state.storeDetails.name}`,
          type: 'success',
        })
      } catch (err) {
        $message({
          message: err.message,
          type: 'error',
        })
      }
    }

    return apollo.defaultClient
      .mutate({
        mutation: SaveStoreNote,
        variables: { fields: note },
        refetchQueries: {
          query: StoreDetailsQuery,
          variables: { id: state.storeDetails.id },
        },
      })
      .then(() => {
        if (!noMessage) {
          $message({
            message: `Store Issue Note ${note.isDelete ? 'Deleted' : 'Saved'}`,
            type: 'success',
          })
        }
      })
      .catch((err) => {
        $message({
          message: err,
          type: 'error',
        })
      })
  },

  async saveStoreWifi({ dispatch }, { $message }) {
    apollo.defaultClient
      .mutate({
        mutation: SaveStoreWifi,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            wpaEnterpriseUsername: state.editableStoreFields.wpaEnterpriseUsername,
            networkSecurityType: state.editableStoreFields.networkSecurityType,
            psk: state.editableStoreFields.psk,
            wpaePassword: state.editableStoreFields.wpaePassword,
            ssid: state.editableStoreFields.ssid,
          },
        },
      })
      .then(({ data: { saveStoreWifi: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      }).catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async saveStoreInstallFields({ dispatch }, { $message }) {
    await apollo.defaultClient
      .mutate({
        mutation: SaveStoreInstallFields,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            isInstallerWifiTest: state.editableStoreFields.isInstallerWifiTest,
          },
        },
      }).then(({ data: { saveStoreInstallFields: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async saveStoreInstallLanes({ commit, dispatch }, { $confirm, $message }) {
    const removedLanes = state.storeDetails.lanes.filter(
      lane => !state.lanes.find(l => (l.id === lane.id)),
    )

    if (removedLanes.length) {
      try {
        await $confirm('Are you sure you want to delete these lanes?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning',
        })
      } catch (err) {
        // Revert all changes.
        commit('setEditableStoreFields', state.storeDetails)
        commit('setLanes', state.storeDetails)
      }
    }

    await apollo.defaultClient
      .mutate({
        mutation: SaveStoreInstallLanes,
        variables: {
          storeId: state.editableStoreFields.id,
          lanes: state.lanes.map(lane => Object.assign(lane, { number: +(lane.number) })),
        },
      }).then(({ data: { saveStoreInstallLanes: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async setStoreInstalled({ dispatch }, { $message }) {
    await apollo.defaultClient
      .mutate({
        mutation: SetStoreInstalled,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            installDate: state.editableStoreFields.installDate,
            isInstalled: state.editableStoreFields.isInstalled,
          },
        },
      }).then(({ data: { setStoreInstalled: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async setStoreLocations({ commit }, { storeLocations }) {
    commit('setStoreLocations', storeLocations)
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/store/StoreDetails.js